import { useEffect, useState } from "react";
import {
    fetchTerritorySalesByCategoryGroup,
    fetchTerritoryBudgetByYear,
} from "../services/orderAnalytics.service";
import { getProRataProgress } from '../helpers/helper'

export function useTerritorySalesMetrics({
    territoryId,
    dateParams,
    year,
    isDateRangeIncomplete,
}) {
    const [data, setData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (
            !territoryId ||
            isDateRangeIncomplete ||
            !dateParams?.from ||
            !dateParams?.to
        ) {
            setData({});
            return;
        }

        let mounted = true;

        const load = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const [salesRes, budgetRes] = await Promise.all([
                    fetchTerritorySalesByCategoryGroup(dateParams),
                    fetchTerritoryBudgetByYear({ year }),
                ]);

                if (!mounted) return;

                let salesRetail = 0;
                let salesProject = 0;
                let budgetRetail = 0;
                let budgetProject = 0;

                (salesRes || [])
                    .filter(s => String(s.territory) === territoryId)
                    .forEach(s => {
                        if (s.category === "RET") salesRetail += s.total || 0;
                        if (s.category === "PRJ") salesProject += s.total || 0;
                    });

                (budgetRes || [])
                    .filter(b => String(b.territory) === territoryId)
                    .forEach(b => {
                        if (b.categoryGroup === "RET") budgetRetail += b.budget || 0;
                        if (b.categoryGroup === "PRJ") budgetProject += b.budget || 0;
                    });

                const salesTotal = salesRetail + salesProject;
                const budgetTotal = budgetRetail + budgetProject;
                
                const { elapsedDays, totalDays } = getProRataProgress(
                    dateParams?.filterType,
                    dateParams?.to,
                    year
                );
                
                const proRataBudget = (elapsedDays / totalDays) * budgetTotal;

                const grandTotalSales = (salesRes || []).reduce(
                    (sum, s) => sum + (s.total || 0),
                    0
                );

                setData({
                    salesRetail,
                    salesProject,
                    salesTotal,
                    budgetRetail,
                    budgetProject,
                    budgetTotal,

                    actualVsBudget:
                        budgetTotal > 0 ? (salesTotal / budgetTotal) * 100 : null,

                    actualVsProRata:
                        proRataBudget > 0 ? (salesTotal / proRataBudget) * 100 : null,

                    salesShare:
                        grandTotalSales > 0
                            ? (salesTotal / grandTotalSales) * 100
                            : null,
                    retailAchieved:
                        budgetRetail > 0 ? (salesRetail / budgetRetail) * 100 : null,

                    projectAchieved:
                        budgetProject > 0 ? (salesProject / budgetProject) * 100 : null,

                });
            } catch (e) {
                if (mounted) {
                    setError("Unable to load territory metrics");
                    setData({});
                }
            } finally {
                if (mounted) setIsLoading(false);
            }
        };

        load();
        return () => {
            mounted = false;
        };
    }, [territoryId, dateParams?.from, dateParams?.to, dateParams?.filterType, year, isDateRangeIncomplete]);

    return { data, isLoading, error };
}